import {
  WesterosNoteTypeCreateDocument,
  WesterosNoteTypeDeleteDocument,
  WesterosNoteTypeGetByIdDocument,
  WesterosNoteTypeListDocument,
  WesterosNoteTypesPaginatedDocument,
  WesterosNoteTypeUpdateDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  ICreateNoteTypeVars,
  IGetNoteTypeByIdReturnType,
  IGetNotesListArgs,
  INoteTypeModel,
  INotesListReturnType,
  IUpdateNoteTypeVars,
} from "api/services/library/note-type";
import { ResourceEnum, ScopeEnum } from "config";

export async function getWesterosNoteTypesList(
  variables: IGetNotesListArgs
): Promise<INotesListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosNoteTypesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosNoteTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createWesterosNoteType(
  variables: ICreateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosNoteTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all note type queries so they refetch with the new item
    executeEviction(client, {
      typename: "WesterosNoteType",
      fieldNames: ["westerosNoteTypesPaginated", "westerosNoteTypeList"],
    });

    return res?.data?.westerosNoteTypeCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateWesterosNoteType(
  variables: IUpdateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosNoteTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "WesterosNoteType",
      id: variables.noteTypeId.toString(),
      fieldNames: [
        "westerosNoteTypesPaginated",
        "westerosNoteTypeList",
        "westerosNoteTypeGetById",
      ],
    });

    return res?.data?.westerosNoteTypeUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteWesterosNoteType(
  noteTypeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosNoteTypeDeleteDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "WesterosNoteType",
      id: noteTypeId.toString(),
      fieldNames: [
        "westerosNoteTypesPaginated",
        "westerosNoteTypeList",
        "westerosNoteTypeGetById",
      ],
    });

    return Boolean(res?.data?.westerosNoteTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getWesterosNoteType(
  noteTypeId: number
): Promise<IGetNoteTypeByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosNoteTypeGetByIdDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosNoteTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getWesterosNoteTypes(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<INoteTypeModel, "noteTypeId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosNoteTypeListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosNoteTypeList ?? [];
  } catch (error) {
    throw error;
  }
}
